import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiChevronLeft } from 'react-icons/fi';
import { auth, db } from '../../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import TopBar from '../TopBar';
import { useAddPlayerStatsForm } from './useAddPlayerStatsForm';
import ModeSelector from './ModeSelector';
import QuickStatsForm from './QuickStatsForm';
import StatsTable from './StatsTable';
import SubmitButton from './SubmitButton';
import ScreenshotImporter from '../PlayerStatsScreenshotImporter/ScreenshotImporter';
import {
  hasMeaningfulControlPlayerStatsRows,
  mapControlPreviewRowsToFormRows,
} from '../PlayerStatsScreenshotImporter/utils/controlPreviewToFormRows';
import { seasonMapPools } from './constants';
import { getTodayDate } from './utils';
import {
  MATCH_TYPE_CATEGORIES,
  findMatchTypeEntry,
  getMatchTypeOptions,
  getMatchTypeRegions,
} from '../../utils/matchTypeCatalog';
import '../../styles/AddPlayerStatsForm.css';

const AddPlayerStatsForm = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('quick');
  const [showScreenshotImporter, setShowScreenshotImporter] = useState(false);
  const [showAllColumns, setShowAllColumns] = useState(false);
  const [topUserProfile, setTopUserProfile] = useState(null);
  const [topTeamLogo, setTopTeamLogo] = useState(null);
  const [matchDate, setMatchDate] = useState(getTodayDate());
  const [matchType, setMatchType] = useState('SCRIMMAGE');
  const [matchCategory, setMatchCategory] = useState('PRACTICE');
  const [matchTypeRegion, setMatchTypeRegion] = useState('GLOBAL');
  const [region, setRegion] = useState('');
  const [vsTeam, setVsTeam] = useState('');
  const [step, setStep] = useState(1);
  const matchYear = matchDate
    ? String(new Date(matchDate).getFullYear())
    : String(new Date().getFullYear());
  const latestSeasonKey = Object.keys(seasonMapPools).sort().reverse()[0];
  const prefillAppliedRef = useRef(false);
  const metaHydratedRef = useRef(false);

  const {
    teamId,
    activeMode,
    setActiveMode,
    structuredData,
    setStructuredData,
    activePlayers,
    playerImages,
    matchTypes,
    matchTypeCatalog,
    vsTeams,
    regions,
    isSubmitting,
    isEdit,
    prefillMeta,
    inputRefs,
    updateCell,
    handleSubmit
  } = useAddPlayerStatsForm();

  const matchTypeRegionOptions = useMemo(() => {
    const options = getMatchTypeRegions(matchTypeCatalog, matchCategory);
    return [...new Set([matchTypeRegion, ...options, 'GLOBAL'].filter(Boolean))];
  }, [matchTypeCatalog, matchCategory, matchTypeRegion]);

  const matchTypeOptions = useMemo(() => {
    const catalogOptions = getMatchTypeOptions(matchTypeCatalog, {
      category: matchCategory,
      region: matchTypeRegion,
    }).map(item => item.label);
    return [...new Set([matchType, ...catalogOptions, ...(!catalogOptions.length ? matchTypes : [])].filter(Boolean))];
  }, [matchTypeCatalog, matchCategory, matchTypeRegion, matchType, matchTypes]);

  const selectedMatchTypeEntry = useMemo(() => (
    findMatchTypeEntry(matchTypeCatalog, {
      label: matchType,
      category: matchCategory,
      region: matchTypeRegion,
    })
  ), [matchTypeCatalog, matchType, matchCategory, matchTypeRegion]);

  const scoreForLabel = activeMode === 'SND' || activeMode === 'CTL' ? 'Your Rounds' : 'Your Score';
  const scoreAgainstLabel = activeMode === 'SND' || activeMode === 'CTL' ? 'Opponent Rounds' : 'Opponent Score';
  const screenshotImportControl = activeMode === 'CTL' ? (
    <button
      type="button"
      onClick={() => setShowScreenshotImporter(true)}
      className="player-import-screenshots-btn"
    >
      Import Screenshots
    </button>
  ) : null;
  const viewToggleControl = (
    <button
      type="button"
      onClick={() => setViewMode(viewMode === 'quick' ? 'table' : 'quick')}
      className="player-view-toggle-btn"
    >
      {viewMode === 'quick' ? 'Full Table' : 'Quick Form'}
    </button>
  );

  const handleApplyControlImport = useCallback((reviewedRows) => {
    if (activeMode !== 'CTL') {
      toast.error('Control OCR imports can only be applied while Control mode is active.');
      return false;
    }

    if (!Array.isArray(reviewedRows) || reviewedRows.length === 0) {
      toast.error('No reviewed Control rows are available to apply.');
      return false;
    }

    if (hasMeaningfulControlPlayerStatsRows(structuredData)) {
      const shouldReplace = window.confirm(
        'Replace existing player stats? Applying this screenshot import will replace the current Control player rows.'
      );
      if (!shouldReplace) return false;
    }

    const nextRows = mapControlPreviewRowsToFormRows(reviewedRows, structuredData);
    setViewMode('quick');
    setShowAllColumns(true);
    setStep(3);
    setStructuredData(nextRows);
    setShowScreenshotImporter(false);
    toast.success(`${nextRows.length} players applied to Player Stats form.`);
    return true;
  }, [activeMode, structuredData, setStructuredData]);

  useEffect(() => {
    if (!selectedMatchTypeEntry) return;
    if (selectedMatchTypeEntry.category !== matchCategory) {
      setMatchCategory(selectedMatchTypeEntry.category);
    }
    if ((selectedMatchTypeEntry.region || 'GLOBAL') !== matchTypeRegion) {
      setMatchTypeRegion(selectedMatchTypeEntry.region || 'GLOBAL');
    }
  }, [selectedMatchTypeEntry, matchCategory, matchTypeRegion]);

  // Hydrate local match meta once from loaded rows (prevents Date ping-pong loops)
  useEffect(() => {
    if (!structuredData.length) {
      metaHydratedRef.current = false;
      return;
    }
    if (metaHydratedRef.current) return;
    const first = structuredData[0];
    if (first?.Date && first.Date !== matchDate) setMatchDate(first.Date);
    if (first?.MatchType && first.MatchType !== matchType) setMatchType(first.MatchType);
    if (first?.MatchCategory && first.MatchCategory !== matchCategory) setMatchCategory(first.MatchCategory);
    if (first?.MatchTypeRegion && first.MatchTypeRegion !== matchTypeRegion) setMatchTypeRegion(first.MatchTypeRegion);
    if (first?.Region && first.Region !== region) setRegion(first.Region);
    if (first?.VS && first.VS !== vsTeam) setVsTeam(first.VS);
    metaHydratedRef.current = true;
    // Intentionally only react to loaded row changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structuredData, isEdit]);

  // propagate matchDate to all rows
  useEffect(() => {
    if (!structuredData.length) return;
    const first = structuredData[0];
    if (first?._rowId) {
      const rowId = first._rowId;
      if (first.Date !== matchDate) updateCell(rowId, 'Date', matchDate);
      if (first.MatchType !== matchType) updateCell(rowId, 'MatchType', matchType);
      if (first.MatchTypeId !== (selectedMatchTypeEntry?.id || '')) updateCell(rowId, 'MatchTypeId', selectedMatchTypeEntry?.id || '');
      if (first.MatchCategory !== (selectedMatchTypeEntry?.category || matchCategory || '')) updateCell(rowId, 'MatchCategory', selectedMatchTypeEntry?.category || matchCategory || '');
      if (first.MatchTypeRegion !== (selectedMatchTypeEntry?.region || matchTypeRegion || '')) updateCell(rowId, 'MatchTypeRegion', selectedMatchTypeEntry?.region || matchTypeRegion || '');
      if (first.Region !== region) updateCell(rowId, 'Region', region);
      if (first.VS !== vsTeam) updateCell(rowId, 'VS', vsTeam);
    }
  }, [matchDate, matchType, matchCategory, matchTypeRegion, selectedMatchTypeEntry, region, vsTeam, structuredData, updateCell]);

  // Apply prefill meta when editing a match
  useEffect(() => {
    if (!prefillMeta || prefillAppliedRef.current) return;
    if (!structuredData.length || !structuredData[0]?._rowId) return;
    if (prefillMeta.matchDate) setMatchDate(prefillMeta.matchDate);
    if (prefillMeta.matchType) setMatchType(prefillMeta.matchType);
    if (prefillMeta.region) setRegion(prefillMeta.region);
    if (prefillMeta.vsTeam) setVsTeam(prefillMeta.vsTeam);
    // push map/fields into first row
    const id = structuredData[0]._rowId;
    if (prefillMeta.map) updateCell(id, 'Map', prefillMeta.map);
    if (prefillMeta.region) updateCell(id, 'Region', prefillMeta.region);
    if (prefillMeta.vsTeam) updateCell(id, 'VS', prefillMeta.vsTeam);
    if (prefillMeta.matchType) updateCell(id, 'MatchType', prefillMeta.matchType);
    if (prefillMeta.matchDate) updateCell(id, 'Date', prefillMeta.matchDate);
    // jump to table view and unlock step 3
    setViewMode('quick');
    setShowAllColumns(true);
    setStep(3);
    prefillAppliedRef.current = true;
  }, [prefillMeta, structuredData, updateCell]);

  // Advance steps when required fields are set
  useEffect(() => {
    if (matchDate && matchType && region && vsTeam) {
      setStep(2);
    } else {
      setStep(1);
    }
  }, [matchDate, matchType, region, vsTeam]);

  useEffect(() => {
    const fetchTopBarData = async () => {
      const user = auth?.currentUser;
      if (!user) return;

      const userRef = doc(db, `users/${user.uid}`);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const data = userSnap.data();
        setTopUserProfile(data);

        if (data.teamId) {
          const teamRef = doc(db, `teams/${data.teamId}`);
          const teamSnap = await getDoc(teamRef);
          if (teamSnap.exists()) {
            setTopTeamLogo(teamSnap.data().logo);
          }
        }
      }
    };

    fetchTopBarData();
  }, []);

  if (!teamId) {
    return (
      <>
        <TopBar
          userProfile={topUserProfile}
          teamLogo={topTeamLogo}
          setUserProfile={setTopUserProfile}
          setTeamLogo={setTopTeamLogo}
        />
        <div className="player-stats-loading">
          <p>Loading team information...</p>
        </div>
      </>
    );
  }

  return (
    <>
      <TopBar
        userProfile={topUserProfile}
        teamLogo={topTeamLogo}
        setUserProfile={setTopUserProfile}
        setTeamLogo={setTopTeamLogo}
      />

      <main className="player-stats-page">
        <div className="player-stats-shell">
          <button
            type="button"
            onClick={() => {
              sessionStorage.setItem('returnToTab', 'statshub');
              navigate('/my-stats');
            }}
            className="player-stats-back-button"
          >
            <FiChevronLeft size={18} />
            Back to Stats Hub
          </button>

          <header className="player-stats-header">
            <div>
              <p className="player-stats-eyebrow">Player Data Entry</p>
              <h2>Add Player Stats</h2>
              <p>Quick entry form optimized for fast data entry.</p>
            </div>

            <div className="player-stats-header-links">
              <button
                type="button"
                onClick={() => navigate('/settings?tab=database-config')}
              >
                Manage active players
              </button>
              <span>|</span>
              <button
                type="button"
                onClick={() => navigate('/settings?tab=database-config')}
              >
                Edit match types
              </button>
            </div>
          </header>

          <section className="player-step-card">
            <div className="player-step-header">
              <div className="player-step-title">
                <span>Step 1</span>
                <strong>Match Setup</strong>
              </div>
              <p>Autosaves as you type</p>
            </div>

            <div className="player-match-grid">
              <label className="player-stats-field">
                <span>Match Date</span>
                <input
                  type="date"
                  value={matchDate}
                  onChange={(e) => setMatchDate(e.target.value)}
                />
              </label>

              <label className="player-stats-field">
                <span>Match Category</span>
                <select
                  value={matchCategory}
                  onChange={(e) => {
                    setMatchCategory(e.target.value);
                    setMatchType('');
                  }}
                >
                  {MATCH_TYPE_CATEGORIES.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>

              <label className="player-stats-field">
                <span>Competition Region</span>
                <select
                  value={matchTypeRegion}
                  onChange={(e) => {
                    setMatchTypeRegion(e.target.value);
                    setMatchType('');
                  }}
                >
                  {matchTypeRegionOptions.map((value) => (
                    <option key={value} value={value}>{value}</option>
                  ))}
                </select>
              </label>

              <label className="player-stats-field player-stats-field-wide">
                <span>Competition</span>
                <select
                  value={matchType}
                  onChange={(e) => {
                    const nextMatchType = e.target.value;
                    const entry = findMatchTypeEntry(matchTypeCatalog, {
                      label: nextMatchType,
                      category: matchCategory,
                      region: matchTypeRegion,
                    });
                    setMatchType(nextMatchType);
                    if (entry) {
                      setMatchCategory(entry.category);
                      setMatchTypeRegion(entry.region || 'GLOBAL');
                    }
                  }}
                >
                  <option value="">Select competition</option>
                  {matchTypeOptions.map((m, i) => (
                    <option key={`${m}-${i}`} value={m}>{m}</option>
                  ))}
                </select>
              </label>

              <label className="player-stats-field">
                <span>Opponent Region</span>
                <select
                  value={region}
                  onChange={(e) => { setRegion(e.target.value); setVsTeam(''); }}
                >
                  <option value="">Select opponent region</option>
                  {[...new Set([...(regions || []), region].filter(Boolean))].map((r, i) => <option key={i} value={r}>{r}</option>)}
                </select>
              </label>

              <label className="player-stats-field">
                <span>Opponent Team</span>
                <select
                  value={vsTeam}
                  onChange={(e) => setVsTeam(e.target.value)}
                  disabled={!region}
                >
                  <option value="">Select opponent team</option>
                  {[
                    ...new Set([
                      ...vsTeams.filter(t => t.region === region).map((t) => t.name),
                      vsTeam,
                    ].filter(Boolean)),
                  ].map((teamName, i) => <option key={i} value={teamName}>{teamName}</option>)}
                </select>
              </label>
            </div>
          </section>

          <section className={`player-step-card ${step < 2 ? 'is-disabled' : ''}`}>
            <div className="player-step-header">
              <div className="player-step-title">
                <span>Step 2</span>
                <strong>Mode & Map</strong>
              </div>
              <p>Defaults to last-used choices</p>
            </div>

            <div className="player-step-body" style={{ pointerEvents: step < 2 ? 'none' : 'auto' }}>
              <ModeSelector
                activeMode={activeMode}
                setActiveMode={setActiveMode}
              />

              <div className="player-mode-grid">
                <label className="player-stats-field player-map-field">
                  <span>Map</span>
                  <select
                    value={structuredData[0]?.Map || ''}
                    onChange={(e) => {
                      if (!structuredData.length) return;
                      updateCell(structuredData[0]._rowId, 'Map', e.target.value);
                    }}
                  >
                    <option value="">Select map</option>
                    {((seasonMapPools[matchYear] && seasonMapPools[matchYear][activeMode]) || seasonMapPools[latestSeasonKey]?.[activeMode] || []).map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </label>

                <label className="player-stats-field">
                  <span>Match Result</span>
                  <select
                    value={structuredData[0]?.WinLose || ''}
                    onChange={(e) => {
                      if (!structuredData.length) return;
                      updateCell(structuredData[0]._rowId, 'WinLose', e.target.value);
                    }}
                  >
                    <option value="">Select result</option>
                    <option value="WIN">WIN</option>
                    <option value="LOSE">LOSE</option>
                  </select>
                </label>

                <label className="player-stats-field">
                  <span>{scoreForLabel}</span>
                  <input
                    type="number"
                    min="0"
                    value={structuredData[0]?.PF ?? ''}
                    onChange={(e) => {
                      if (!structuredData.length) return;
                      updateCell(structuredData[0]._rowId, 'PF', e.target.value);
                    }}
                  />
                </label>

                <label className="player-stats-field">
                  <span>{scoreAgainstLabel}</span>
                  <input
                    type="number"
                    min="0"
                    value={structuredData[0]?.PA ?? ''}
                    onChange={(e) => {
                      if (!structuredData.length) return;
                      updateCell(structuredData[0]._rowId, 'PA', e.target.value);
                    }}
                  />
                </label>
              </div>
            </div>
          </section>

          <section className={`player-stats-workspace ${step < 2 ? 'is-disabled' : ''}`}>
            <div className="player-step-header">
              <div className="player-step-title">
                <span>Step 3</span>
                <strong>Player Stats</strong>
              </div>
              <p>Enter performance for the active lineup</p>
            </div>

            {activeMode && (
              <>
                {viewMode === 'quick' ? (
                  <QuickStatsForm
                    activeMode={activeMode}
                    structuredData={structuredData}
                    updateCell={updateCell}
                    activePlayers={activePlayers}
                    playerImages={playerImages}
                    matchTypes={matchTypes}
                    vsTeams={vsTeams}
                    inputRefs={inputRefs}
                    showAllColumns={showAllColumns}
                    setShowAllColumns={setShowAllColumns}
                    viewToggleControl={viewToggleControl}
                    importControl={screenshotImportControl}
                  />
                ) : (
                  <>
                    <div className="quick-actions-bar player-table-view-toolbar">
                      <div className="quick-actions-left">
                        <strong>Full Table</strong>
                      </div>
                      <div className="quick-actions-right">
                        {screenshotImportControl}
                        {viewToggleControl}
                      </div>
                    </div>
                    <StatsTable
                      activeMode={activeMode}
                      structuredData={structuredData}
                      updateCell={updateCell}
                      activePlayers={activePlayers}
                      playerImages={playerImages}
                      matchTypes={matchTypes}
                      vsTeams={vsTeams}
                      inputRefs={inputRefs}
                    />
                  </>
                )}

                <SubmitButton
                  isSubmitting={isSubmitting}
                  onSubmit={handleSubmit}
                  disabled={!activeMode || structuredData.length === 0}
                  label={isEdit ? 'Save Player Stats ->' : 'Submit Player Stats ->'}
                />
              </>
            )}
          </section>
        </div>
      </main>

      {showScreenshotImporter && (
        <ScreenshotImporter
          mode={activeMode}
          activePlayers={activePlayers}
          onApplyControlImport={handleApplyControlImport}
          onClose={() => setShowScreenshotImporter(false)}
        />
      )}
    </>
  );
};

export default AddPlayerStatsForm;
